import { useState, useCallback, useEffect } from 'react';
import { BankingService } from 'services';
import { ITransaction, ETransactionStatus } from 'common/transactions.interface';
import { useSnackbar } from 'notistack';
import { DateTime } from 'luxon';
import { EAccountType } from 'common/accounts.interface';

const bankingService = new BankingService();

const convertStatusToFilter = (status: string): string | undefined => {
  if (status === 'Todos') return undefined;

  // Map ReceivePage statuses to backend transaction statuses
  if (status === 'PAGO') {
    return ETransactionStatus.CONFIRMED;
  }
  if (status === 'PENDENTE') {
    return ETransactionStatus.SENT_TO_PROVIDER;
  }
  if (status === 'CANCELADO') {
    return ETransactionStatus.CANCELED;
  }
  if (status === 'ERRO_EMISSAO') {
    return ETransactionStatus.ERROR;
  }

  // For A_PAGAR and EM_ATRASO, use non-paid statuses
  if (status === 'A_PAGAR' || status === 'EM_ATRASO') {
    return [ETransactionStatus.PROCESSING, ETransactionStatus.SENT_TO_PROVIDER].join(',');
  }

  return status;
};

export const usePaginatedBankSlips = (params: {
  status: string;
  type: string;
  startDate: DateTime;
  endDate: DateTime;
  useDueDateFilter?: boolean;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { status, type, startDate, endDate, useDueDateFilter = false } = params;

  const [transactions, setTransactions] = useState<ITransaction[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const statusFilter = convertStatusToFilter(status);

      let typeFilter: string | undefined;
      if (type === 'Boleto') {
        typeFilter = 'BANK_SLIP_IN';
      } else if (type === 'Pix') {
        typeFilter = 'PIX_PAYMENT_IN';
      } else {
        // 'Todas' - fetch both incoming types
        typeFilter = 'BANK_SLIP_IN,PIX_PAYMENT_IN';
      }

      // Adjust date filters for past due and upcoming statuses
      let adjustedStartDate = startDate;
      let adjustedEndDate = endDate;
      const today = DateTime.now().startOf('day');

      if (status === 'EM_ATRASO') {
        // For overdue: only show items with due date before today
        adjustedEndDate = endDate < today ? endDate : today.minus({ days: 1 });
      } else if (status === 'A_PAGAR') {
        // For upcoming: only show items with due date from today onwards
        adjustedStartDate = startDate > today ? startDate : today;
      }

      const response = await bankingService.getTransactions({
        page: page + 1,
        accountType: EAccountType.CONTROL,
        limit: rowsPerPage,
        status: statusFilter,
        type: typeFilter,
        ...(useDueDateFilter
          ? {
              payDueDateStart: adjustedStartDate ? `${adjustedStartDate.toISODate()}T00:00:00` : undefined,
              payDueDateEnd: adjustedEndDate ? `${adjustedEndDate.toISODate()}T23:59:59` : undefined,
            }
          : {
              startDate: adjustedStartDate ? `${adjustedStartDate.toISODate()}T00:00:00` : undefined,
              endDate: adjustedEndDate ? `${adjustedEndDate.toISODate()}T23:59:59` : undefined,
            }),
      });

      setTransactions(response.items);
      setTotal(response.total);
    } catch {
      enqueueSnackbar('Erro ao carregar recebimentos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, status, type, startDate, endDate, enqueueSnackbar, useDueDateFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [status, startDate.toISODate(), endDate.toISODate()]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    transactions,
    loading,
    page,
    rowsPerPage,
    total,
    setPage,
    setRowsPerPage,
    reload: load,
  };
};
